// features/campaigns/store/campaign.selectors.ts
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CampaignState } from './campaign.reducer';
import { Campaign } from '@core/models/campaign.model';
import { CampaignFilters, CampaignStats } from '@core/services/facades/campaign.facade';
import { VIDEO_PLATFORM_TYPES } from '@core/constants/video-platforms.const';

export const selectCampaignState = createFeatureSelector<CampaignState>('campaigns');

// Base selectors
export const selectAllCampaigns = createSelector(selectCampaignState, (state) => state.campaigns);

export const selectCampaignPages = createSelector(selectCampaignState, (state) => state.pages);

export const selectSelectedCampaign = createSelector(selectCampaignState, (state) => state.selectedCampaign);

export const selectCampaignFilters = createSelector(selectCampaignState, (state) => state.filters);

export const selectCampaignLoading = createSelector(selectCampaignState, (state) => state.loading);

export const selectCampaignsLoaded = createSelector(selectCampaignState, (state) => state.loaded);

export const selectCampaignError = createSelector(selectCampaignState, (state) => state.error);

export const selectVisiblePageIndex = createSelector(selectCampaignState, (state) => state.visiblePageIndex);

export const selectListScrollPosition = createSelector(selectCampaignState, (state) => state.listScrollPosition);

// Filtered campaigns
export const selectFilteredCampaigns = createSelector(selectAllCampaigns, selectCampaignFilters, (campaigns, filters) =>
    applyFilters(campaigns, filters),
);

export const selectFilteredPages = createSelector(selectFilteredCampaigns, (filteredCampaigns) => {
    const pageSize = 15;
    const pages: { page: number; data: Campaign[] }[] = [];

    for (let i = 0; i < filteredCampaigns.length; i += pageSize) {
        pages.push({
            page: pages.length + 1,
            data: filteredCampaigns.slice(i, i + pageSize),
        });
    }

    return pages.length > 0 ? pages : [{ page: 1, data: [] }];
});

export const selectDisplayedPages = createSelector(selectFilteredPages, selectVisiblePageIndex, (pages, visiblePageIndex) =>
    pages.slice(0, Math.min(visiblePageIndex, pages.length - 1) + 1),
);

export const selectHasMorePages = createSelector(
    selectFilteredPages,
    selectVisiblePageIndex,
    (pages, visiblePageIndex) => visiblePageIndex < pages.length - 1,
);

// Campaign by ID
export const selectCampaignById = (id: string) =>
    createSelector(selectAllCampaigns, (campaigns) => campaigns.find((c) => c.insertion_order_id === id));

// Statistics
export const selectCampaignStats = createSelector(selectFilteredCampaigns, (campaigns) => calculateStats(campaigns));

// All campaigns statistics (unfiltered)
export const selectAllCampaignStats = createSelector(selectAllCampaigns, (campaigns) => calculateStats(campaigns));

// Campaigns by status
export const selectActiveCampaigns = createSelector(selectAllCampaigns, (campaigns) => campaigns.filter((c) => c.isActive));

export const selectIncomingCampaigns = createSelector(selectAllCampaigns, (campaigns) => campaigns.filter((c) => c.isIncoming));

export const selectEndedCampaigns = createSelector(selectAllCampaigns, (campaigns) => campaigns.filter((c) => c.isEnded));

// Campaigns by type
export const selectCampaignsByType = (type: string) => createSelector(selectAllCampaigns, (campaigns) => campaigns.filter((c) => c.type === type));

// Available campaign types
export const selectAvailableCampaignTypes = createSelector(selectAllCampaigns, (campaigns) => [...new Set(campaigns.map((c) => c.type))]);

// Advertiser list
export const selectAdvertisers = createSelector(selectAllCampaigns, (campaigns) => {
    const advertisersMap = new Map<string, string>();
    campaigns.forEach((c) => {
        advertisersMap.set(c.client_id, c.advertiser_name);
    });
    return Array.from(advertisersMap.entries()).map(([id, name]) => ({
        id,
        name,
    }));
});

// Helper functions
function applyFilters(campaigns: Campaign[], filters: CampaignFilters): Campaign[] {
    let filtered = [...campaigns];

    if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        filtered = filtered.filter((campaign) => campaign.insertion_order_name.toLowerCase().includes(term));
    }

    if (filters.types && filters.types.length > 0) {
        // Legacy TF1/M6/NETFLIX campaigns are matched by the VIDEO filter
        const types = filters.types.includes('VIDEO') ? [...filters.types, ...VIDEO_PLATFORM_TYPES] : filters.types;
        filtered = filtered.filter((campaign) => types.includes(campaign.type));
    }

    if (filters.statuses && filters.statuses.length > 0) {
        filtered = filtered.filter((campaign) =>
            filters.statuses!.some((status) => {
                switch (status) {
                    case 'active':
                        return campaign.isActive;
                    case 'incoming':
                        return campaign.isIncoming && !campaign.is_stopped;
                    case 'stopped':
                        return campaign.is_stopped;
                    case 'ended':
                        return campaign.isEnded && !campaign.isIncoming && !campaign.is_stopped;
                    default:
                        return false;
                }
            }),
        );
    }

    if (filters.clientIds && filters.clientIds.length > 0) {
        filtered = filtered.filter((campaign) => filters.clientIds!.includes(campaign.client_id));
    }

    if (filters.dateRange) {
        const { start, end } = filters.dateRange;

        if (start && !end) {
            // Seulement date de début : campagnes qui commencent à cette date
            filtered = filtered.filter((campaign) => {
                const campaignStart = new Date(campaign.begin_date);
                return campaignStart.toDateString() === start.toDateString();
            });
        } else if (!start && end) {
            // Seulement date de fin : campagnes qui se terminent à cette date
            filtered = filtered.filter((campaign) => {
                const campaignEnd = new Date(campaign.end_date);
                return campaignEnd.toDateString() === end.toDateString();
            });
        } else if (start && end) {
            // Les deux dates : campagnes dans la plage
            filtered = filtered.filter((campaign) => {
                const campaignStart = new Date(campaign.begin_date);
                const campaignEnd = new Date(campaign.end_date);
                return campaignStart >= start && campaignEnd <= end;
            });
        }
    }
    return filtered;
}

function calculateStats(campaigns: Campaign[]): CampaignStats {
    return {
        totalCampaigns: campaigns.length,
        activeCampaigns: campaigns.filter((c) => c.isActive).length,
        totalBudget: campaigns.reduce((sum, c) => sum + c.budget + c.budget_offer, 0),
        totalSpent: campaigns.reduce((sum, c) => sum + c.budget_spent, 0),
        campaignsByType: campaigns.reduce(
            (acc, c) => {
                acc[c.type] = (acc[c.type] || 0) + 1;
                return acc;
            },
            {} as { [type: string]: number },
        ),
    };
}
